// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import { useNavigate, useSearchParams } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useToast } from "@chotca/ui";
import type { Gender } from "@chotca/config";
import {
  EmployerAnalyticsEvent,
  venueTypeFromBusinessType,
} from "@chotca/config";
import { ChotcaApiError } from "@chotca/sdk";
import { postingToCreateValues } from "@chotca/posting-form";
import { logAnalyticsEvent } from "@/lib/analytics";
import { useMe, useOrg, usePosting } from "@chotca/api-client";
import { useCreatePosting, useUploadPostingImage } from "@chotca/api-client";
import {
  usePostingTemplates,
  useCreatePostingTemplate,
} from "@chotca/api-client";
import {
  useArchiveOrgAddress,
  useCreateOrgAddress,
  useOrgAddresses,
  useUpdateOrgAddress,
} from "@chotca/api-client";
import { PostingForm, type PostingFormOutput } from "@/components/posting-form";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";
import { PhoneVerifyDialog } from "@/components/employer/phone-verify-dialog";
import * as React from "react";

export function NewPostingForm() {
  const nav = useNavigate();
  const { openUpgrade } = useSubscriptionModals();
  const [params] = useSearchParams();
  // "Đăng lại" carries the source posting id here. We fetch it and prefill the
  // create form so the user only has to review and post — no editing from
  // scratch. Works for both open and expired/closed tins.
  const duplicateId = params.get("duplicate");
  const sourceQ = usePosting(duplicateId);
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId ?? "";
  // The posting form no longer asks "Loại quán" — it inherits the venue type
  // from the org's businessType (captured at org creation).
  const org = useOrg(orgId || null);
  const defaultVenueType = venueTypeFromBusinessType(org.data?.businessType);
  const create = useCreatePosting(orgId);
  const uploadPreview = useUploadPostingImage(orgId);
  const templates = usePostingTemplates(orgId || null);
  const saveTemplate = useCreatePostingTemplate(orgId);
  const addressesQ = useOrgAddresses(orgId || null);
  const createAddress = useCreateOrgAddress(orgId);
  const updateAddress = useUpdateOrgAddress(orgId);
  const archiveAddress = useArchiveOrgAddress(orgId);
  const { toast } = useToast();
  const [phoneOpen, setPhoneOpen] = React.useState(false);

  // Publishing needs U1_CONTACT (email + phone verified) — SCRUM-74. A posting
  // exposes an applicant's real phone, so a verified human must stand behind it.
  const needsVerify = (me.data?.identityLevel ?? "U0_NEW") === "U0_NEW";

  async function handleSubmit(values: PostingFormOutput) {
    if (!orgId) {
      toast({
        tone: "error",
        title: "Chưa có doanh nghiệp",
        description: "Hãy hoàn tất onboarding trước khi đăng tin.",
      });
      return;
    }
    if (needsVerify) {
      setPhoneOpen(true);
      return;
    }
    try {
      await create.mutateAsync({
        position: values.position,
        title: values.title,
        description: values.description,
        venueType: values.venueType,
        employmentType: values.employmentType,
        salaryMin: values.salaryMin,
        salaryMax: values.salaryMax,
        salaryType: values.salaryType,
        headcount: values.headcount,
        benefits: values.benefits,
        skillsRequired: values.skillsRequired,
        skillsPreferred: values.skillsPreferred,
        requirements: {
          gender: (values.reqGender ?? null) as Gender | null,
          minAge: values.reqMinAge ?? null,
          maxAge: values.reqMaxAge ?? null,
          minHeightCm: values.reqMinHeightCm ?? null,
          motorbikeRequired: values.reqMotorbike,
          excludeVisibleTattoo: values.reqExcludeTattoo,
          kycCccdRequired: values.reqKyc,
        },
        softNotes: values.softNotes || null,
        previewImageKey: values.previewImageKey ?? null,
        addressIds: values.addressIds,
      });
    } catch (err) {
      if (
        err instanceof ChotcaApiError &&
        err.code === "open_posting_limit_reached"
      ) {
        const limit = (err.details as { limit?: number } | undefined)?.limit;
        toast({
          tone: "warning",
          title: "Đã đạt giới hạn tin đang mở của gói",
          description: limit
            ? `Gói hiện tại cho tối đa ${limit} tin. Đóng bớt tin hoặc nâng cấp gói để đăng thêm.`
            : "Đóng bớt tin hoặc nâng cấp gói để đăng thêm.",
        });
        // Give a direct path to fix it.
        openUpgrade();
        return;
      }
      // Server-side U1 gate (stale client state) — open the verify dialog.
      if (err instanceof ChotcaApiError && err.code === "identity_required") {
        setPhoneOpen(true);
        return;
      }
      throw err;
    }
    toast({ tone: "success", title: "Đã đăng tin thành công" });
    logAnalyticsEvent(EmployerAnalyticsEvent.postingPublished, {
      position: values.position,
      employment_type: values.employmentType,
      headcount: values.headcount,
    });
    nav("/postings");
  }

  const toFormTemplate = (
    t: NonNullable<typeof templates.data>["items"][number],
  ) => ({
    id: t.id,
    name: t.name,
    label: t.label ?? null,
    position: t.position,
    venueType: t.venueType,
    employmentType: t.employmentType,
    title: t.title,
    description: t.description,
    salaryMin:
      typeof t.salaryMin === "number" ? t.salaryMin : Number(t.salaryMin),
    salaryMax:
      typeof t.salaryMax === "number" ? t.salaryMax : Number(t.salaryMax),
    salaryType: t.salaryType,
    defaultHeadcount: t.defaultHeadcount,
    benefits: t.benefits,
    skillsRequired: t.skillsRequired,
    skillsPreferred: t.skillsPreferred,
    requirements: t.requirements
      ? {
          gender:
            t.requirements.gender === "MALE" ||
            t.requirements.gender === "FEMALE"
              ? t.requirements.gender
              : null,
          minAge: t.requirements.minAge ?? null,
          maxAge: t.requirements.maxAge ?? null,
          minHeightCm: t.requirements.minHeightCm ?? null,
          motorbikeRequired: t.requirements.motorbikeRequired ?? false,
          excludeVisibleTattoo: t.requirements.excludeVisibleTattoo ?? false,
          kycCccdRequired: t.requirements.kycCccdRequired ?? false,
        }
      : undefined,
    softNotes: t.softNotes,
  });

  // While the source posting loads (duplicate flow), show a skeleton so the
  // form mounts once with prefilled defaultValues rather than empty-then-reset.
  if (duplicateId && sourceQ.isPending) {
    return (
      <section className="space-y-6">
        <div className="h-10 w-2/3 animate-pulse rounded bg-ink/10" />
        <div className="h-64 animate-pulse rounded-xl bg-ink/5" />
      </section>
    );
  }

  const initialValues =
    duplicateId && sourceQ.data
      ? postingToCreateValues(sourceQ.data)
      : undefined;

  return (
    <>
      <PostingForm
        key={initialValues ? `dup-${duplicateId}` : "new"}
        initialValues={initialValues}
        defaultVenueType={defaultVenueType}
        loading={create.isPending}
        onSubmit={handleSubmit}
        onUploadPreviewImage={async (file, onProgress) => {
          const r = await uploadPreview.mutateAsync({ file, onProgress });
          return { key: r.key, url: r.publicUrl };
        }}
        addressBundle={{
          addresses: addressesQ.data?.items ?? [],
          loading: addressesQ.isLoading,
          onCreate: (input) => createAddress.mutateAsync(input),
          onUpdate: (id, input) =>
            updateAddress.mutateAsync({ id, body: input }),
          onArchive: async (id) => {
            await archiveAddress.mutateAsync(id);
          },
        }}
        savedTemplates={
          templates.data?.items
            .filter((t) => !t.isSystem)
            .map(toFormTemplate) ?? []
        }
        systemTemplates={
          templates.data?.items.filter((t) => t.isSystem).map(toFormTemplate) ??
          []
        }
        manageTemplatesHref="/postings/templates"
        onManageTemplates={() => nav("/postings/templates")}
        onSaveAsTemplate={async (v, name) => {
          await saveTemplate.mutateAsync({
            name,
            position: v.position,
            venueType: v.venueType,
            employmentType: v.employmentType,
            title: v.title,
            description: v.description,
            salaryMin: v.salaryMin,
            salaryMax: v.salaryMax,
            salaryType: v.salaryType,
            defaultHeadcount: v.headcount,
            benefits: v.benefits,
            skillsRequired: v.skillsRequired,
            skillsPreferred: v.skillsPreferred,
            requirements: {
              gender: (v.reqGender ?? null) as Gender | null,
              minAge: v.reqMinAge ?? null,
              maxAge: v.reqMaxAge ?? null,
              minHeightCm: v.reqMinHeightCm ?? null,
              motorbikeRequired: v.reqMotorbike,
              excludeVisibleTattoo: v.reqExcludeTattoo,
              kycCccdRequired: v.reqKyc,
            },
            softNotes: v.softNotes || null,
          });
        }}
      />
      <PhoneVerifyDialog
        open={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        initialPhone={me.data?.phone}
      />
    </>
  );
}
